import { ArrowLeft, Info } from 'lucide-react'
import { useI18n } from '../i18n'
import { formatMoneyCents as fmtCents } from '../utils/money'
import { benefitsDate } from '../utils/benefitsView'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

/**
 * MyInsuranceView — PROD-581 (insurance level 1). What the "My insurance" screen draws, from
 * utils/benefitsView.js buildBenefitsView. Presentational only: the page
 * (pages/profile/MyInsurance.jsx) owns the stores, the Re-check call and navigation.
 *
 * Every number is the payer's, with its date; a figure the payer did not report says so
 * (V-6) and is never a 0 or another figure; nothing is added up (V-7); no "after deductible"
 * (PROD-584); no age warning (V-3); a network chip only when the stored answer names the network
 * of THAT figure (PROD-587), never from the first line's flag.
 *
 * Props
 *   view        buildBenefitsView(...) of the coverage shown
 *   copy        { headline, body } from utils/eligibilityCopy.js (negative / other states)
 *   tier        'primary' | 'secondary'; onTier(tier) — the switch is drawn when hasSecondary
 *   hasSecondary
 *   noInsurance true when no carrier is on file (self-pay or nothing entered)
 *   recheck     { availableAt: ISO | null, busy: bool, notice: string | null, onRecheck() }
 *   onBack(), onOpenInsurance()
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE = '#E9F8FF'
const LBLUE = '#A7D9FC'
const MUTED = '#5B6E80'
const GREEN_INK = '#2F9E4A'
const SLATE_INK = '#64748B'

function NetworkChip({ network, t }) {
  if (network === 'in') return <span data-net="in" className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ color: GREEN_INK, background: 'rgba(102,209,112,0.14)' }}>{t('doctors.inNetwork')}</span>
  if (network === 'out') return <span data-net="out" className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ color: SLATE_INK, background: '#F1F5F9' }}>{t('eligibility.outOfNetwork')}</span>
  if (network === 'not_stated') return <span data-net="not_stated" className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded-full" style={{ color: SLATE_INK, background: '#F1F5F9' }}>{t('myInsurance.networkNotStated')}</span>
  return null
}

function Row({ label, value, network, t }) {
  return (
    <div className="flex items-center justify-between gap-2 py-2" style={{ borderBottom: '1px solid #EEF2F6' }}>
      <span className="text-[12.5px] font-semibold" style={{ color: MUTED }}>{label}</span>
      <span className="flex items-center gap-1.5">
        <NetworkChip network={network} t={t} />
        <span className="text-[13px] font-bold" style={{ color: value == null ? SLATE_INK : NAVY }}>{value == null ? t('myInsurance.notReported') : value}</span>
      </span>
    </div>
  )
}

function spendValue(node, t) {
  if (!node) return null
  const { total, remaining } = node
  if (total != null && remaining != null) {
    const met = Math.max(0, total - remaining)
    return `${fmtCents(met)} ${t('eligibility.met')} · ${fmtCents(remaining)} ${t('eligibility.toGo')} · ${fmtCents(total)}`
  }
  if (total != null) return fmtCents(total)
  if (remaining != null) return `${fmtCents(remaining)} ${t('eligibility.remaining')}`
  return null
}

function Card({ title, children }) {
  return (
    <div className="rounded-[16px] mb-3.5 overflow-hidden" style={{ border: `1.5px solid ${LBLUE}` }}>
      {title && <div className="px-3.5 py-2.5 text-[12.5px] font-bold" style={{ background: ICE, color: NAVY }}>{title}</div>}
      <div className="px-3.5 pb-1">{children}</div>
    </div>
  )
}

export default function MyInsuranceView({ view, copy, tier = 'primary', onTier, hasSecondary = false, noInsurance = false, recheck = {}, onBack, onOpenInsurance }) {
  const { t, language } = useI18n()
  const v = view || { state: 'none' }
  const date = benefitsDate(v.checkedAt, language)
  const busy = !!recheck.busy
  const blockedUntil = recheck.availableAt || null

  // the secondary coverage has its own check path on Profile -> Insurance: no button here
  const recheckButton = (labelKey) => (!recheck.onRecheck ? null : (
    <button
      data-recheck
      onClick={() => !busy && !blockedUntil && recheck.onRecheck()}
      disabled={busy || !!blockedUntil}
      className="w-full mt-1 rounded-[13px] py-3 text-[14px] font-bold flex items-center justify-center gap-2"
      style={{ background: busy || blockedUntil ? '#F1F5F9' : BLUE, color: busy || blockedUntil ? SLATE_INK : '#FFFFFF', border: 'none' }}
    >
      {busy && <OrellaSpinner size={16} tone="inherit" />}
      {busy ? t('eligibility.verifyingCoverage') : t(labelKey)}
    </button>
  ))

  let body
  if (noInsurance) {
    body = (
      <Card>
        <p className="text-[13px] py-3" style={{ color: NAVY }}>{t('myInsurance.noInsurance')}</p>
        <button data-open-insurance onClick={onOpenInsurance} className="w-full mb-3 rounded-[13px] py-3 text-[14px] font-bold" style={{ background: BLUE, color: '#FFFFFF', border: 'none' }}>{t('myInsurance.addInsurance')}</button>
      </Card>
    )
  } else if (v.state === 'none') {
    body = (
      <Card>
        <p className="text-[13px] py-3" style={{ color: NAVY }}>{t('myInsurance.neverChecked')}</p>
        <div className="mb-3">{recheckButton('myInsurance.checkNow')}</div>
      </Card>
    )
  } else if (v.state !== 'active') {
    body = (
      <Card>
        {copy?.headline && <p className="text-[14px] font-bold pt-3" style={{ color: NAVY }}>{copy.headline}</p>}
        {copy?.body && <p className="text-[12.5px] mt-1 leading-relaxed" style={{ color: MUTED }}>{copy.body}</p>}
        {date && <p data-as-of className="text-[11px] mt-2" style={{ color: SLATE_INK }}>{t('eligibility.asOf', { when: date })}</p>}
        <button data-open-insurance onClick={onOpenInsurance} className="w-full my-3 rounded-[13px] py-3 text-[14px] font-bold" style={{ background: BLUE, color: '#FFFFFF', border: 'none' }}>{t('myInsurance.updateInsurance')}</button>
      </Card>
    )
  } else {
    const fam = (kind) => v[kind]?.family
    body = (
      <>
        {/* 1. the estimate line, always first wherever a number is shown */}
        <p data-estimate className="text-[12px] leading-relaxed mb-3 flex gap-1.5" style={{ color: MUTED }}>
          <Info size={14} className="shrink-0 mt-[1px]" style={{ color: BLUE }} />
          <span>{t('myInsurance.estimateLine')}</span>
        </p>
        {/* 2. plan and date */}
        <div className="mb-3.5">
          {(v.planName || v.planType) && (
            <div className="text-[15px] font-extrabold" style={{ color: NAVY }}>{[v.planName, v.planType].filter(Boolean).join(' · ')}</div>
          )}
          <div data-as-of className="text-[11px] mt-0.5" style={{ color: SLATE_INK }}>{t('eligibility.asOf', { when: date || '—' })}</div>
        </div>
        {/* 3. per visit */}
        <Card title={t('myInsurance.perVisitTitle')}>
          {v.perVisit.map((r) => (
            <Row key={r.id} label={t(`myInsurance.${r.id}`)} value={r.cents == null ? null : fmtCents(r.cents)} network={r.network} t={t} />
          ))}
        </Card>
        {/* 4.-5. deductible and out-of-pocket: individual up front, family apart (V-5) */}
        {[['deductible', 'eligibility.deductible'], ['outOfPocket', 'eligibility.outOfPocketMax']].map(([kind, key]) => (
          <Card key={kind} title={t(key)}>
            <Row label={t('eligibility.levelIndividual')} value={spendValue(v[kind]?.individual, t)} network={v[kind]?.individual?.network ?? null} t={t} />
            {fam(kind) && (
              <details data-family={kind} className="py-1">
                <summary className="text-[12px] font-bold py-1.5" style={{ color: BLUE }}>{t('eligibility.levelFamily')}</summary>
                <Row label={t('eligibility.levelFamily')} value={spendValue(fam(kind), t)} network={fam(kind).network} t={t} />
              </details>
            )}
          </Card>
        ))}
        {/* 6. coinsurance: the payer's percentage only (PROD-584) */}
        <Card>
          <Row label={t('eligibility.coinsurance')} value={v.coinsurance ? `${v.coinsurance.pct}%` : null} network={v.coinsurance?.network ?? null} t={t} />
          {v.referralLikely && <p className="text-[11.5px] py-2" style={{ color: MUTED }}>{t('myInsurance.referralLikely', { type: v.referralLikely })}</p>}
        </Card>
        {/* 8. other services the payer reported a copay for */}
        {v.otherServices.length > 0 && (
          <details data-other-services className="mb-3.5">
            <summary className="text-[12.5px] font-bold py-2" style={{ color: BLUE }}>{t('myInsurance.otherServices')}</summary>
            {v.otherServices.map((s, i) => (
              <Row key={i} label={s.label} value={fmtCents(s.cents)} network={s.network} t={t} />
            ))}
          </details>
        )}
        {/* 9. where it comes from */}
        <p data-source className="text-[11px] leading-relaxed mb-3" style={{ color: SLATE_INK }}>{t('myInsurance.sourceLine')}</p>
        {recheckButton('eligibility.recheck')}
      </>
    )
  }

  return (
    <div className="min-h-full bg-white flex flex-col">
      <div className="sticky-page-header px-4 pt-4 pb-2 flex items-center gap-2.5">
        <button onClick={() => onBack && onBack()} style={{ background: 'none', border: 'none' }}>
          <ArrowLeft size={18} color="#64748B" strokeWidth={2.5} />
        </button>
        <span style={{ fontSize: 17, fontWeight: 700, color: NAVY }}>{t('myInsurance.title')}</span>
      </div>
      <div className="flex-1 px-4 pb-10 overflow-auto">
        {hasSecondary && !noInsurance && (
          <div className="flex gap-1.5 mb-3.5" role="tablist">
            {['primary', 'secondary'].map((k) => (
              <button key={k} role="tab" aria-selected={tier === k} data-tier={k} onClick={() => onTier && onTier(k)}
                className="flex-1 rounded-full py-1.5 text-[12.5px] font-bold"
                style={{ background: tier === k ? NAVY : ICE, color: tier === k ? '#FFFFFF' : NAVY, border: 'none' }}>
                {t(`myInsurance.${k}`)}
              </button>
            ))}
          </div>
        )}
        {body}
        {recheck.notice && <p data-recheck-notice className="text-[11.5px] mt-2" style={{ color: SLATE_INK }}>{recheck.notice}</p>}
        {blockedUntil && !busy && <p data-available-on className="text-[11.5px] mt-2 text-center" style={{ color: SLATE_INK }}>{t('myInsurance.availableOn', { date: benefitsDate(blockedUntil, language) })}</p>}
      </div>
    </div>
  )
}
